import React from 'react';
import { Dimensions, ScrollView, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { toHtmlTextStyle } from '../components/general/toHtmlTextStyle';

const WIDTH = Dimensions.get('window').width;
const PAGE_HORIZONTAL_PADDING = WIDTH >= 1200 ? 100 : 24;
const IS_WIDE_LAYOUT = WIDTH >= 1080;

const opportunityCards = [
    {
        icon: 'account-group-outline',
        title: 'Community-led growth',
        description:
            'We want to work with people who understand their local family and camp communities and can help SnapCamp grow market by market.',
    },
    {
        icon: 'handshake-outline',
        title: 'Local market facilitators',
        description:
            'Parents who know their area well can help introduce camp operators to SnapCamp and support early market adoption.',
    },
    {
        icon: 'cash-multiple',
        title: 'Commission-based incentives',
        description:
            'Qualified facilitators can earn commissions for every camp they successfully bring onto the platform.',
    },
];

const CareersScreen = () => {
    return (
        <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
            <View style={styles.container}>
                <View style={styles.hero}>
                    <Text style={styles.eyebrow}>Careers</Text>
                    <View style={styles.heroContent}>
                        <View style={styles.heroTitleWrap}>
                            <h1 style={toHtmlTextStyle(styles.heroTitle, { marginTop: 0 })}>Help grow SnapCamp in your local community.</h1>
                        </View>
                        <View style={styles.heroBodyWrap}>
                            <Text style={styles.heroSubtitle}>
                                We are building a network of people who can help bring more camps, families, and local momentum onto the platform as SnapCamp expands.
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Now recruiting local parents as market facilitators</Text>
                    <Text style={styles.sectionSubtitle}>
                        We are looking for local parents to join our team as market facilitators. If you know strong camps in your area and can help introduce them to SnapCamp, you can earn commissions for every camp you bring onto the platform.
                    </Text>

                    <View style={styles.cardGrid}>
                        {opportunityCards.map((card) => (
                            <View key={card.title} style={styles.card}>
                                <View style={styles.cardIconWrap}>
                                    <MaterialCommunityIcons name={card.icon as any} size={24} color="#1D4ED8" />
                                </View>
                                <Text style={styles.cardTitle}>{card.title}</Text>
                                <Text style={styles.cardDescription}>{card.description}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <View style={styles.callout}>
                    <Text style={styles.calloutTitle}>Who this is a good fit for</Text>
                    <Text style={styles.calloutText}>
                        This role is ideal for parents and community connectors who know local camp operators, care about improving camp discovery for families, and want to be part of growing SnapCamp in their region.
                    </Text>
                </View>

                <View style={styles.contactCard}>
                    <Text style={styles.contactTitle}>Apply or reach out</Text>
                    <Text style={styles.contactDescription}>
                        If you are interested in becoming a market facilitator, send us a note with your city or state, your background, and the kinds of camps or communities you know best.
                    </Text>

                    <View style={styles.contactPill}>
                        <MaterialCommunityIcons name="email-outline" size={18} color="#1D4ED8" />
                        <Text style={styles.contactEmail}>careers@snapcamp.ai</Text>
                    </View>
                </View>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 72,
        paddingHorizontal: PAGE_HORIZONTAL_PADDING,
    },
    container: {
        width: '100%',
    },
    hero: {
        paddingBottom: 32,
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
        marginBottom: 32,
        width: '100%',
    },
    heroContent: {
        flexDirection: IS_WIDE_LAYOUT ? 'row' : 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 28,
    },
    heroTitleWrap: {
        flex: IS_WIDE_LAYOUT ? 1 : undefined,
        maxWidth: IS_WIDE_LAYOUT ? 580 : undefined,
    },
    heroBodyWrap: {
        flex: IS_WIDE_LAYOUT ? 1 : undefined,
        maxWidth: IS_WIDE_LAYOUT ? 500 : undefined,
    },
    eyebrow: {
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '700',
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: '#1D4ED8',
        marginBottom: 12,
    },
    heroTitle: {
        fontSize: 38,
        lineHeight: 46,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 12,
    },
    heroSubtitle: {
        fontSize: 17,
        lineHeight: 28,
        color: '#475569',
    },
    section: {
        marginBottom: 32,
        width: '100%',
    },
    sectionTitle: {
        fontSize: 30,
        lineHeight: 38,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 8,
    },
    sectionSubtitle: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        marginBottom: 22,
        maxWidth: 920,
    },
    cardGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
        justifyContent: 'space-between',
    },
    card: {
        width: IS_WIDE_LAYOUT ? '31.8%' : '100%',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 22,
        padding: 24,
        backgroundColor: '#FFFFFF',
    },
    cardIconWrap: {
        width: 48,
        height: 48,
        borderRadius: 16,
        backgroundColor: '#EFF6FF',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    cardTitle: {
        fontSize: 20,
        lineHeight: 28,
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: 10,
    },
    cardDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
    callout: {
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 24,
        padding: 28,
        marginBottom: 32,
        backgroundColor: '#F8FAFC',
    },
    calloutTitle: {
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 8,
    },
    calloutText: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        maxWidth: 860,
    },
    contactCard: {
        borderWidth: 1,
        borderColor: '#DBEAFE',
        backgroundColor: '#F8FBFF',
        borderRadius: 24,
        padding: 28,
    },
    contactTitle: {
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 8,
    },
    contactDescription: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        marginBottom: 18,
        maxWidth: 760,
    },
    contactPill: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        borderRadius: 999,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#BFDBFE',
    },
    contactEmail: {
        fontSize: 15,
        lineHeight: 22,
        fontWeight: '700',
        color: '#1D4ED8',
    },
});

export default CareersScreen;
